import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Clock, Banknote } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage() {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [content, setContent] = useState({});

  useEffect(() => {
    axios.get(`${API}/services`).then(r => setServices(r.data)).catch(() => {});
    axios.get(`${API}/content`).then(r => setContent(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar transparent content={content} />

      {/* Hero */}
      <section data-testid="hero-section" className="relative h-screen min-h-[600px] overflow-hidden">
        <img
          src={content.hero_image || "https://static.prod-images.emergentagent.com/jobs/a581d965-98ba-47e4-9616-12fe194260f5/images/2ba02d3087c500e22c4f0dff58eb9f6c336262b672b28b0857c25a19bf1b3d53.png"}
          alt="Imassasje velvære"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="hero-overlay absolute inset-0" />
        <div className="relative z-10 flex flex-col justify-end h-full max-w-7xl mx-auto px-6 md:px-12 pb-20 md:pb-28">
          <p className="text-white/60 text-sm tracking-[0.3em] uppercase mb-4 animate-fade-up animate-delay-1">
            {content.hero_label}
          </p>
          <h1 data-testid="hero-title" className="font-serif text-5xl sm:text-6xl lg:text-8xl text-white font-medium tracking-tight animate-fade-up animate-delay-2">
            {content.hero_title || "Imassasje"}
          </h1>
          <p data-testid="hero-subtitle" className="text-white/80 text-base md:text-lg max-w-lg mt-4 leading-relaxed animate-fade-up animate-delay-3">
            {content.hero_subtitle || "Opplev dyp avspenning og velvære"}
          </p>
          <div className="mt-8 animate-fade-up animate-delay-4">
            <Button
              data-testid="hero-book-btn"
              onClick={() => navigate("/bestill")}
              className="rounded-full bg-white text-foreground hover:bg-white/90 px-8 py-6 text-base font-medium transition-all hover:scale-105 shadow-xl"
            >
              {content.btn_hero || "Book din time"} <ArrowRight className="ml-2" size={18} />
            </Button>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="tjenester" data-testid="services-section" className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-sm tracking-[0.3em] uppercase text-muted-foreground mb-3">{content.services_label || "Våre behandlinger"}</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight mb-16">{content.services_title || "Tjenester"}</h2>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, i) => (
              <div
                key={service.id}
                data-testid={`service-card-${i}`}
                className="service-card group bg-card rounded-2xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:translate-y-[-4px] transition-transform duration-300 cursor-pointer"
                onClick={() => navigate("/bestill", { state: { serviceId: service.id } })}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={service.image_url}
                    alt={service.name}
                    className="service-card-img w-full h-full object-cover"
                  />
                </div>
                <div className="p-8">
                  <h3 className="font-serif text-2xl md:text-3xl mb-3">{service.name}</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">{service.description}</p>
                  <div className="flex items-center gap-6 text-sm text-muted-foreground">
                    <span className="flex items-center gap-2"><Banknote size={16} /> {service.price} kr</span>
                    <span className="flex items-center gap-2"><Clock size={16} /> {service.duration} min</span>
                  </div>
                  <div className="mt-6">
                    <span className="inline-flex items-center gap-2 text-sm font-medium text-foreground group-hover:gap-3 transition-all">
                      {content.service_card_cta || "Book denne behandlingen"} <ArrowRight size={16} />
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About */}
      <section id="om-oss" data-testid="about-section" className="py-20 md:py-32 bg-secondary/40">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
            <div>
              <p className="text-sm tracking-[0.3em] uppercase text-muted-foreground mb-3">{content.about_label || "Vår filosofi"}</p>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight mb-8">
                {content.about_title || "Om Imassasje"}
              </h2>
              <p data-testid="about-text" className="text-muted-foreground leading-relaxed text-base md:text-lg">
                {content.about_text || "Hos Imassasje handler alt om din velvære."}
              </p>
              <div className="mt-10">
                <Button
                  data-testid="about-book-btn"
                  onClick={() => navigate("/bestill")}
                  className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-6 text-base transition-all hover:scale-105 shadow-lg shadow-primary/20"
                >
                  {content.btn_about || "Bestill time"} <ArrowRight className="ml-2" size={18} />
                </Button>
              </div>
            </div>
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img
                src={content.about_image || "https://static.prod-images.emergentagent.com/jobs/a581d965-98ba-47e4-9616-12fe194260f5/images/116635001f09bfbfa2a9ab83e7ec84aaba1eb1515b93ad6a521f1ac00bc0d344.png"}
                alt="Imassasje interiør"
                className="w-full h-full object-cover aspect-[3/4] md:aspect-[4/5]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="cta-section" className="py-20 md:py-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight mb-6">
            {content.booking_title || "Book din time"}
          </h2>
          <p className="text-muted-foreground text-base md:text-lg mb-10 max-w-lg mx-auto">
            {content.booking_description || "Velg den behandlingen som passer deg best og finn en tid som passer."}
          </p>
          <Button
            data-testid="cta-book-btn"
            onClick={() => navigate("/bestill")}
            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-10 py-6 text-lg transition-all hover:scale-105 shadow-lg shadow-primary/20"
          >
            {content.btn_cta || "Book nå"} <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </section>

      <Footer content={content} />
    </div>
  );
}
